import { Dispatch, SetStateAction, useEffect, useState } from "react";

/*
 * reference: https://usehooks-typescript.com/
 */

type SetValue<T> = Dispatch<SetStateAction<T>>;

interface StorageValue<T> {
    value: T;
    expire: number;
}

function useLocalStorage<T>(key: string, initialValue: T, tts: number = 2592000000): [T, SetValue<T>] {
    const readValue = (): T => {
        if (typeof window === "undefined") {
            return initialValue;
        }

        try {
            const item = window.localStorage.getItem(key);
            if (!item) return initialValue;

            const { value, expire }: StorageValue<T> = JSON.parse(item);

            // 만료 시간 체크
            if (Date.now() > expire) {
                window.localStorage.removeItem(key);
                return initialValue;
            }

            return value;
        } catch (error) {
            console.warn(`Error reading localStorage key "${key}":`, error);
            return initialValue;
        }
    };

    const [storedValue, setStoredValue] = useState<T>(readValue);

    const setValue: SetValue<T> = (value) => {
        if (typeof window === "undefined") {
            console.warn(`Tried setting localStorage key "${key}" even though environment is not a client`);
        }

        try {
            const newValue = value instanceof Function ? value(storedValue) : value;
            const obj: StorageValue<T> = {
                value: newValue,
                expire: Date.now() + tts,
            };

            if (value) {
                window.localStorage.setItem(key, JSON.stringify(obj));
            } else {
                window.localStorage.removeItem(key);
            }

            setStoredValue(newValue);
            window.dispatchEvent(new Event("local-storage"));
        } catch (error) {
            console.warn(`Error setting localStorage key "${key}":`, error);
        }
    };

    useEffect(() => {
        setStoredValue(readValue());
    }, []);

    useEffect(() => {
        const handleStorageChange = () => {
            setStoredValue(readValue());
        };

        window.addEventListener("storage", handleStorageChange);
        window.addEventListener("local-storage", handleStorageChange);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
            window.removeEventListener("local-storage", handleStorageChange);
        };
    }, []);

    return [storedValue, setValue];
}

export default useLocalStorage;
